<template>
    <div v-if="show" class="modal-backdrop" @click.self="close">
        <div class="relative flex flex-col max-h-[90vh] bg-n-alpha-3 shadow-md modal-container rtl:text-right skip-context-menu rounded-xl w-[32rem]">
            <div class="modal-header border-b-2 border-n-slate-4 dark:border-n-slate-7 flex-shrink-0">
                <h3 class="modal-title">{{ modalTitle }}</h3>
                <button
                    class="z-10 ltr:right-2 rtl:left-2 top-2 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
                    @click="close">
                    <span class="i-lucide-x flex-shrink-0"></span>
                </button>
            </div>
            <div class="modal-body p-6 overflow-auto flex-1">
                <div v-if="loading" class="loading-overlay">
                    <div class="spinner"></div>
                </div>

                <div v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div class="flex justify-between items-center">
                        <p class="text-sm text-red-600 m-0">{{ error }}</p>
                        <button @click="error = null" class="text-red-600 hover:text-red-800 text-sm font-medium">
                            Fechar
                        </button>
                    </div>
                </div>

                <form @submit.prevent="submitForm">
                    <div class="mb-5">
                        <label class="block mb-1.5 text-sm font-medium text-n-slate-12" for="event-title">Título <span class="text-red-500">*</span></label>
                        <input id="event-title" v-model="formData.title" type="text" 
                               class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" 
                               required placeholder="Adicione um título para o evento" />
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                        <div>
                            <label class="block mb-1.5 text-sm font-medium text-n-slate-12" for="event-start">Data e hora de início <span class="text-red-500">*</span></label>
                            <input id="event-start" v-model="formData.startDateTime" type="datetime-local"
                                   class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" 
                                   required />
                        </div>

                        <div>
                            <label class="block mb-1.5 text-sm font-medium text-n-slate-12" for="event-end">Data e hora de término <span class="text-red-500">*</span></label>
                            <input id="event-end" v-model="formData.endDateTime" type="datetime-local"
                                   class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1" 
                                   required />
                        </div>
                    </div>

                    <div class="mb-5">
                        <button
                            type="button"
                            @click="toggleAllDay"
                            class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 flex justify-between items-center cursor-pointer"
                        >
                            <span class="text-sm font-medium text-n-slate-12">Evento de dia inteiro</span>
                            <input 
                                type="checkbox"
                                v-model="formData.allDay"
                                class="w-4 h-4 text-n-brand bg-n-background border-n-slate-4 rounded focus:ring-n-brand dark:focus:ring-n-brand dark:ring-offset-slate-800 focus:ring-2 dark:bg-slate-700 dark:border-slate-600 pointer-events-none"
                                @click.stop
                            />
                        </button>
                    </div>

                    <div class="mb-5">
                        <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Profissional</label>
                        <div class="relative professional-dropdown">
                            <button
                                ref="professionalTrigger"
                                @click.stop="toggleProfessionalDropdown"
                                class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 flex justify-between items-center text-left"
                                type="button"
                            >
                                <div class="flex items-center overflow-hidden">
                                    <div class="w-6 h-6 flex items-center justify-center rounded-full mr-2 flex-shrink-0 text-white"
                                         :style="{ backgroundColor: getSelectedProfessionalColor() }">
                                        <i class="i-lucide-user text-sm"></i>
                                    </div>
                                    <span class="truncate">
                                        {{ getSelectedProfessionalDisplayText() }}
                                    </span>
                                </div>
                                <i class="i-lucide-chevron-down ml-2 flex-shrink-0 transition-transform" :class="{ 'rotate-180': showProfessionalDropdown }"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Professional Dropdown - Teleported -->
                    <TeleportedDropdown
                        :show="showProfessionalDropdown"
                        :trigger-ref="$refs.professionalTrigger"
                        :max-height="256"
                        @click.stop>
                        <!-- Empty option -->
                        <div class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer flex-shrink-0"
                             @click.stop="selectProfessional('')">
                            <div class="flex items-center">
                                <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 bg-slate-50 text-slate-600 dark:bg-slate-700 dark:text-slate-100">
                                    <i class="i-lucide-user-x"></i>
                                </div>
                                <div>
                                    <div class="text-sm font-medium text-slate-900 dark:text-slate-100">Sem profissional</div>
                                    <div class="text-xs text-slate-500 dark:text-slate-400">Não selecionar um profissional específico</div>
                                </div>
                            </div>
                        </div>

                        <!-- Professionals scrollable list -->
                        <div class="overflow-y-auto flex-1">
                            <div v-for="professional in professionals" :key="professional.id"
                                 class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                                 @click.stop="selectProfessional(professional.id)">
                                <div class="flex items-center">
                                    <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 text-white"
                                         :style="{ backgroundColor: getProfessionalColor(professional) }">
                                        <i class="i-lucide-user"></i>
                                    </div>
                                    <div>
                                        <div class="text-sm font-medium text-slate-900 dark:text-slate-100">{{ professional.name }}</div>
                                        <div v-if="professional.whatsapp" class="text-xs text-slate-500">
                                            {{ formatPhone(professional.whatsapp) }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TeleportedDropdown>

                    <div class="mb-5">
                        <label class="block mb-1.5 text-sm font-medium text-n-slate-12">Contato</label>
                        <div class="relative contact-dropdown">
                            <button
                                ref="contactTrigger"
                                @click.stop="toggleContactDropdown"
                                class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 flex justify-between items-center text-left"
                                type="button"
                            >
                                <div class="flex items-center overflow-hidden">
                                    <div class="w-6 h-6 flex items-center justify-center rounded-full mr-2 flex-shrink-0 bg-n-slate-2 text-n-slate-11">
                                        <i class="i-lucide-user-circle text-sm"></i>
                                    </div>
                                    <span class="truncate">
                                        {{ getSelectedContactDisplayText() }}
                                    </span>
                                </div>
                                <i class="i-lucide-chevron-down ml-2 flex-shrink-0 transition-transform" :class="{ 'rotate-180': showContactDropdown }"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Contact Dropdown - Teleported -->
                    <TeleportedDropdown
                        :show="showContactDropdown"
                        :trigger-ref="$refs.contactTrigger"
                        :max-height="320"
                        @click.stop>
                        <!-- Empty option -->
                        <div class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer flex-shrink-0"
                             @click.stop="selectContact('')">
                            <div class="flex items-center">
                                <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 bg-slate-50 text-slate-600 dark:bg-slate-700 dark:text-slate-100">
                                    <i class="i-lucide-user-x"></i>
                                </div>
                                <div>
                                    <div class="text-sm font-medium text-slate-900 dark:text-slate-100">Sem contato</div>
                                    <div class="text-xs text-slate-500 dark:text-slate-400">Não selecionar um contato específico</div>
                                </div>
                            </div>
                        </div>

                        <!-- Search bar -->
                        <div class="p-2 border-b border-slate-200 dark:border-slate-600 flex-shrink-0">
                            <div class="relative">
                                <input
                                    v-model="contactSearchQuery"
                                    type="text"
                                    placeholder="Buscar contato..."
                                    class="w-full pl-10 pr-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                    @click.stop
                                />
                            </div>
                        </div>

                        <!-- Contacts scrollable list -->
                        <div class="overflow-y-auto flex-1">
                            <div v-for="contact in filteredContacts" :key="contact.id"
                                 class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                                 @click.stop="selectContact(contact.id)">
                                <div class="flex items-center">
                                    <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400">
                                        <i class="i-lucide-user-circle"></i>
                                    </div>
                                    <div>
                                        <div
                                            class="text-sm font-medium text-slate-900 dark:text-slate-100"
                                            v-dompurify-html="formatContactName(contact.name || 'Sem nome')"
                                        ></div>
                                        <div v-if="contact.phone_number" class="text-xs text-slate-500 dark:text-slate-400">
                                            {{ formatPhone(contact.phone_number) }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TeleportedDropdown>

                    <div class="mb-5">
                        <label class="block mb-1.5 text-sm font-medium text-n-slate-12" for="event-description">Descrição</label>
                        <textarea id="event-description" v-model="formData.description"
                                  class="w-full px-3 py-2.5 border border-n-slate-4 dark:border-n-slate-7 rounded-lg text-sm bg-n-background text-n-slate-12 focus:border-n-brand hover:bg-n-slate-1 resize-y"
                                  rows="3" placeholder="Adicione uma descrição (opcional)"></textarea>
                    </div>

                    <div class="mb-5">
                        <EmailListView
                            v-model="attendeesFormatted"
                            title="E-mails dos Participantes"
                            input-placeholder="Digite um email e pressione Enter"
                            empty-message="Nenhum participante adicionado"
                        />
                    </div>

                </form>

                <!-- Confirm Delete Modal -->
                <div v-if="showDeleteConfirmation" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]">
                    <div class="bg-white dark:bg-slate-800 rounded-xl p-6 w-96 max-w-[90vw] shadow-lg">
                        <h3 class="text-lg font-semibold text-n-slate-12 mb-2">Confirmar exclusão</h3>
                        <p class="text-sm text-n-slate-11 mb-6">Tem certeza que deseja excluir este evento?</p>
                        <div class="flex justify-end gap-3">
                            <button type="button"
                                class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                                @click="showDeleteConfirmation = false" :disabled="loading">
                                Cancelar
                            </button>
                            <button type="button"
                                class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-red-600 text-white hover:enabled:bg-red-700 focus-visible:bg-red-700 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                                @click="deleteEvent" :disabled="loading">
                                <span v-if="loading" class="animate-spin i-lucide-loader"></span>
                                <span>{{ loading ? 'Excluindo...' : 'Excluir' }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex justify-end gap-3 p-4 border-t-2 border-n-slate-4 dark:border-n-slate-7 flex-shrink-0 bg-n-alpha-3">
                <button v-if="event" type="button"
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-red-600 text-white hover:enabled:bg-red-700 focus-visible:bg-red-700 outline-transparent h-10 px-4 text-sm font-medium justify-center mr-auto"
                    @click="confirmDelete" :disabled="loading">
                    Excluir
                </button>
                <button type="button"
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                    @click="close" :disabled="loading">
                    Cancelar
                </button>
                <button type="submit"
                    class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:brightness-110 focus-visible:brightness-110 outline-transparent h-10 px-4 text-sm font-medium justify-center"
                    @click="submitForm" :disabled="loading">
                    <span v-if="loading" class="animate-spin i-lucide-loader"></span>
                    <span>{{ loading ? 'Salvando...' : 'Salvar' }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import EmailListView from 'dashboard/components/EmailListView.vue';
import TeleportedDropdown from './TeleportedDropdown.vue';
import { formatFlagDisplayText } from 'shared/helpers/flagEmoji';

export default {
    name: 'EventModal',
    components: {
        EmailListView,
        TeleportedDropdown
    },
    props: {
        show: Boolean,
        event: Object,
        selectedDate: Date,
        selectedEndDate: Date
    },
    data() {
        return {
            formData: {
                title: '',
                startDateTime: '',
                endDateTime: '',
                description: '',
                allDay: false,
                professionalId: '',
                contactId: '',
                attendees: []
            },
            professionals: [],
            contacts: [],
            contactSearchQuery: '',
            showProfessionalDropdown: false,
            showContactDropdown: false,
            loading: false,
            loadingMessage: 'Carregando...',
            error: null,
            showDeleteConfirmation: false
        };
    },
    computed: {
        modalTitle() {
            return this.event ? 'Editar Evento' : 'Novo Evento';
        },
        eventId() {
            // Make sure we're using the correct event ID format
            if (this.event && this.event.id) {
                // If it's a string, return as is
                if (typeof this.event.id === 'string') {
                    return this.event.id;
                }
                // If it's a number or other type, convert to string
                return String(this.event.id);
            }
            return null;
        },
        filteredContacts() {
            if (!this.contactSearchQuery.trim()) {
                return this.contacts;
            }

            const query = this.contactSearchQuery.toLowerCase().trim();
            return this.contacts.filter(contact => {
                const name = (contact.name || '').toLowerCase();
                const phone = (contact.phone_number || '').toLowerCase();
                return name.includes(query) || phone.includes(query);
            });
        },
        // Converter array de strings (emails) para formato do IntegratedListView
        attendeesFormatted: {
            get() {
                return this.formData.attendees.map(email => ({
                    id: email,
                    name: email
                }));
            },
            set(value) {
                this.formData.attendees = value.map(item => item.name);
            }
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.initializeFormData();
                this.loadProfessionals();
                this.loadContacts();
                this.error = null;
            }
        },
        'formData.startDateTime'(newStartDateTime, oldStartDateTime) {
            if (!newStartDateTime || !this.formData.endDateTime) return;

            try {
                const newStart = new Date(newStartDateTime);
                const currentEnd = new Date(this.formData.endDateTime);

                // Skip if dates are invalid
                if (isNaN(newStart.getTime()) || isNaN(currentEnd.getTime())) return;

                // Only adjust if new start is after current end
                if (newStart >= currentEnd) {
                    let newEnd;

                    // Try to preserve original duration if we have the old start date
                    if (oldStartDateTime) {
                        try {
                            const oldStart = new Date(oldStartDateTime);
                            if (!isNaN(oldStart.getTime())) {
                                const originalDuration = currentEnd.getTime() - oldStart.getTime();
                                newEnd = new Date(newStart.getTime() + originalDuration);

                                // Validate the calculated end date
                                if (isNaN(newEnd.getTime())) {
                                    throw new Error('Invalid calculated end date');
                                }
                            } else {
                                throw new Error('Invalid old start date');
                            }
                        } catch (error) {
                            // Fallback to 1 hour increment
                            newEnd = new Date(newStart.getTime() + (60 * 60 * 1000));
                        }
                    } else {
                        // No old date available, use 1 hour increment
                        newEnd = new Date(newStart.getTime() + (60 * 60 * 1000));
                    }

                    // Update the end date
                    this.formData.endDateTime = this.formatDateForInput(newEnd);
                }
            } catch (error) {
                console.warn('Error updating end date:', error);
            }
        },
        'formData.professionalId'(newProfessionalId) {
            // Adicionar email do profissional automaticamente quando selecionado
            if (newProfessionalId && this.professionals.length > 0) {
                const professional = this.professionals.find(p => String(p.id) === String(newProfessionalId));
                // console.log('Profissional selecionado:', professional);
                if (professional) {
                    // Verificar se o email está em meta_data ou diretamente no objeto
                    const email = professional.meta_data?.email || professional.email;
                    // console.log('Email do profissional:', email);
                    if (email) {
                        const emailLower = email.toLowerCase();
                        // Adicionar apenas se não estiver na lista
                        if (!this.formData.attendees.includes(emailLower)) {
                            this.formData.attendees.push(emailLower);
                            // console.log('Email adicionado aos participantes:', emailLower);
                        }
                        // else {
                        //     console.log('Email já existe na lista de participantes');
                        // }
                    }
                    // else {
                    //     console.warn('Profissional não tem email cadastrado');
                    // }
                }
            }
        }
    },
    methods: {
        formatContactName(name) {
            return formatFlagDisplayText(name || '');
        },
        // Método para formatar números de telefone
        formatPhone(phoneNumber) {
            if (!phoneNumber) return '';

            // Remove todos os caracteres não numéricos
            const cleaned = phoneNumber.replace(/\D/g, '');

            // Verifica o tamanho do número e aplica a formatação adequada
            if (cleaned.length === 11) {
                // Número com DDD e 9 dígitos (ex: 11987654321)
                return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 3)} ${cleaned.slice(3, 7)}-${cleaned.slice(7)}`;
            } else if (cleaned.length === 10) {
                // Número com DDD e 8 dígitos (ex: 1187654321)
                return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
            } else if (cleaned.length === 9) {
                // Número local com 9 dígitos (ex: 987654321)
                return `${cleaned.slice(0, 1)} ${cleaned.slice(1, 5)}-${cleaned.slice(5)}`;
            } else if (cleaned.length === 8) {
                // Número local com 8 dígitos (ex: 87654321)
                return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`;
            } else if (cleaned.length > 11) {
                // Pode ser um número internacional
                return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9, 13)}`;
            }

            // Retornar o número original se não se encaixar nos formatos acima
            return phoneNumber;
        },

        // Professional dropdown methods
        toggleProfessionalDropdown() {
            this.showProfessionalDropdown = !this.showProfessionalDropdown;
            if (this.showProfessionalDropdown) {
                this.showContactDropdown = false;
            }
            this.updateModalScrollLock();
        },

        selectProfessional(professionalId) {
            this.formData.professionalId = professionalId;
            // O watch 'formData.professionalId' cuidará de adicionar o email automaticamente
            this.showProfessionalDropdown = false;
            this.updateModalScrollLock();
        },

        getSelectedProfessionalDisplayText() {
            if (!this.formData.professionalId) {
                return 'Selecione um profissional';
            }
            
            // Convert both to strings for comparison to handle type differences
            const professional = this.professionals.find(p => String(p.id) === String(this.formData.professionalId));
            if (!professional) {
                return 'Profissional não encontrado';
            }
            
            return professional.name;
        },

        // Get the color for the selected professional
        getSelectedProfessionalColor() {
            if (!this.formData.professionalId) {
                return '#94a3b8'; // Default gray color when no professional is selected
            }
            
            const professional = this.professionals.find(p => String(p.id) === String(this.formData.professionalId));
            return this.getProfessionalColor(professional);
        },

        // Get the color for a professional using colorId
        getProfessionalColor(professional) {
            // Define predefined colors matching GeneralProfessionalModal
            const predefinedColors = [
                { colorId: 11, name: 'Tomate', value: '#D50000' },
                { colorId: 4, name: 'Flamingo', value: '#E67C73' },
                { colorId: 6, name: 'Tangerina', value: '#F4511E' },
                { colorId: 5, name: 'Banana', value: '#F6BF26' },
                { colorId: 2, name: 'Sálvia', value: '#33B679' },
                { colorId: 10, name: 'Manjericão', value: '#0B8043' },
                { colorId: 1, name: 'Pavão', value: '#039BE5' },
                { colorId: 9, name: 'Mirtilo', value: '#3F51B5' },
                { colorId: 7, name: 'Lavanda', value: '#7986CB' },
                { colorId: 3, name: 'Uva', value: '#8E24AA' },
                { colorId: 8, name: 'Grafite', value: '#616161' }
            ];
            
            if (professional && professional.meta_data) {
                // Check for new colorId system
                if (professional.meta_data.colorId) {
                    const color = predefinedColors.find(c => c.colorId === professional.meta_data.colorId);
                    if (color) {
                        return color.value;
                    }
                }
                // Fallback to old professional_color if exists
                if (professional.meta_data.professional_color) {
                    return professional.meta_data.professional_color;
                }
            }
            
            return '#039BE5'; // Default to Pavão color
        },

        // Contact dropdown methods
        toggleContactDropdown() {
            this.showContactDropdown = !this.showContactDropdown;
            if (this.showContactDropdown) {
                this.showProfessionalDropdown = false;
                // Clear search when opening dropdown
                this.contactSearchQuery = '';
            }
            this.updateModalScrollLock();
        },

        selectContact(contactId) {
            this.formData.contactId = contactId;
            this.showContactDropdown = false;
            this.contactSearchQuery = '';
            this.updateModalScrollLock();
        },

        getSelectedContactDisplayText() {
            if (!this.formData.contactId) {
                return 'Selecione um contato';
            }

            // Convert both to strings for comparison to handle type differences
            const contact = this.contacts.find(c => String(c.id) === String(this.formData.contactId));
            if (!contact) {
                // console.log('Contato não encontrado! ID procurado:', this.formData.contactId);
                // console.log('IDs disponíveis:', this.contacts.map(c => c.id));
                return 'Contato não encontrado';
            }

            return contact.name || 'Sem nome';
        },

        // Métodos de autenticação
        getCookie(name) {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return parts.pop().split(';').shift();
            return null;
        },
        getAuthHeaders() {
            const cookie = this.getCookie('cw_d_session_info');
            if (!cookie) {
                console.error('cw_d_session_info cookie não encontrado');
                return {};
            }
            try {
                const sessionInfo = JSON.parse(decodeURIComponent(cookie));
                return {
                    'access-token': sessionInfo['access-token'],
                    client: sessionInfo.client,
                    uid: sessionInfo.uid,
                };
            } catch (e) {
                console.error('Erro ao analisar cw_d_session_info:', e);
                return {};
            }
        },

        // Carregar profissionais
        async loadProfessionals() {
            try {
                this.loading = true;
                this.loadingMessage = 'Carregando profissionais...';

                const headers = this.getAuthHeaders();
                const response = await axios.get('/api/v1/professionals', {
                    headers,
                    withCredentials: true
                });

                this.professionals = response.data.data || response.data;

                // Se editando um evento com professional_id, selecioná-lo automaticamente
                if (this.event && this.event.extendedProps && this.event.extendedProps.professional_id) {
                    this.formData.professionalId = this.event.extendedProps.professional_id;
                }

                // SEMPRE adicionar email do profissional se houver um selecionado
                // (tanto para eventos novos quanto para edição)
                if (this.formData.professionalId) {
                    const professional = this.professionals.find(p => String(p.id) === String(this.formData.professionalId));
                    if (professional) {
                        // Verificar se o email está em meta_data ou diretamente no objeto
                        const email = professional.meta_data?.email || professional.email;
                        if (email) {
                            const emailLower = email.toLowerCase();
                            // Adicionar apenas se não estiver na lista
                            if (!this.formData.attendees.includes(emailLower)) {
                                this.formData.attendees.push(emailLower);
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Erro ao carregar profissionais:', error);
                this.error = 'Não foi possível carregar a lista de profissionais.';
            } finally {
                this.loading = false;
            }
        },

        // Carregar contatos
        async loadContacts() {
            try {
                this.loading = true;
                this.loadingMessage = 'Carregando contatos...';

                const headers = this.getAuthHeaders();
                const response = await axios.get('/api/v1/import/contacts', {
                    headers,
                    withCredentials: true
                });

                const allContacts = response.data.contacts || response.data.payload || response.data;
                // Filter out contacts that have "(GROUP)" in their name
                this.contacts = allContacts.filter(contact =>
                    !contact.name || !contact.name.includes('(GROUP)')
                );

                // Se editando um evento com contact_id, selecioná-lo automaticamente
                if (this.event && this.event.extendedProps && this.event.extendedProps.contact_id) {
                    this.formData.contactId = this.event.extendedProps.contact_id;
                    // console.log('Tentando encontrar contato com ID:', this.event.extendedProps.contact_id);
                    // console.log('Contatos carregados:', this.contacts.length);
                    // console.log('Primeiros 3 contatos:', this.contacts.slice(0, 3).map(c => ({ id: c.id, name: c.name })));
                }
            } catch (error) {
                console.error('Erro ao carregar contatos:', error);
                this.error = 'Não foi possível carregar a lista de contatos.';
            } finally {
                this.loading = false;
            }
        },

        // Formatar data para o formato datetime-local
        formatDateForInput(date) {
            if (!date) return '';

            const pad = (num) => (num < 10 ? '0' + num : num);

            const year = date.getFullYear();
            const month = pad(date.getMonth() + 1);
            const day = pad(date.getDate());
            const hours = pad(date.getHours());
            const minutes = pad(date.getMinutes());

            return `${year}-${month}-${day}T${hours}:${minutes}`;
        },

        // Inicializar os dados do formulário
        initializeFormData() {
            if (this.event) {
                // Editar evento existente
                const startDate = new Date(this.event.start);
                const endDate = this.event.end ? new Date(this.event.end) : new Date(startDate.getTime() + (60 * 60 * 1000));

                // Parse attendees string to array
                let attendeesArray = [];
                if (this.event.extendedProps?.attendees) {
                    if (typeof this.event.extendedProps.attendees === 'string') {
                        // Se for string, converter para array
                        attendeesArray = this.event.extendedProps.attendees.split(',').map(email => email.trim()).filter(email => email);
                    } else if (Array.isArray(this.event.extendedProps.attendees)) {
                        // Se já for array, usar diretamente
                        attendeesArray = this.event.extendedProps.attendees;
                    }
                }

                this.formData = {
                    title: this.event.title || '',
                    startDateTime: this.formatDateForInput(startDate),
                    endDateTime: this.formatDateForInput(endDate),
                    description: this.event.extendedProps?.description || '',
                    allDay: this.event.allDay || false,
                    professionalId: this.event.extendedProps?.professional_id || '',
                    contactId: this.event.extendedProps?.contact_id || '',
                    attendees: attendeesArray
                };

                // Debug info
                // console.log('Evento para edição:', {
                //     id: this.eventId,
                //     title: this.event.title,
                //     start: this.event.start,
                //     allDay: this.event.allDay,
                //     professionalId: this.event.extendedProps?.professional_id
                // });
            } else if (this.selectedDate) {
                // Novo evento com data e hora predefinidos
                this.formData = {
                    title: '',
                    startDateTime: this.formatDateForInput(this.selectedDate),
                    endDateTime: this.formatDateForInput(this.selectedEndDate || new Date(this.selectedDate.getTime() + (60 * 60 * 1000))),
                    description: '',
                    allDay: false,
                    professionalId: '',
                    contactId: '',
                    attendees: []
                };
            } else {
                // Reset para valores vazios
                this.formData = {
                    title: '',
                    startDateTime: '',
                    endDateTime: '',
                    description: '',
                    allDay: false,
                    professionalId: '',
                    contactId: '',
                    attendees: []
                };
            }
        },

        // Lidar com a mudança do checkbox de dia inteiro
        handleAllDayToggle() {
            if (this.formData.allDay) {
                // Quando marcado como dia inteiro, ajustar as horas
                const startDate = new Date(this.formData.startDateTime);
                const endDate = new Date(this.formData.endDateTime);

                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);

                this.formData.startDateTime = this.formatDateForInput(startDate);
                this.formData.endDateTime = this.formatDateForInput(endDate);
            }
        },

        // Toggle all day event
        toggleAllDay() {
            this.formData.allDay = !this.formData.allDay;
            this.handleAllDayToggle();
        },

        // Validação do formulário
        validateForm() {
            const start = new Date(this.formData.startDateTime);
            const end = new Date(this.formData.endDateTime);

            // Verificar se as datas são válidas
            if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                this.error = 'Por favor, insira datas e horários válidos.';
                return false;
            }

            // Verificar se a data de término é posterior à data de início
            if (end <= start) {
                this.error = 'A data e hora de término devem ser posteriores à data e hora de início.';
                return false;
            }

            // Verificar se o título não está vazio
            if (!this.formData.title.trim()) {
                this.error = 'O título do evento é obrigatório.';
                return false;
            }


            return true;
        },

        // Enviar o formulário
        async submitForm() {
            if (!this.validateForm()) return;

            // Encontrar a especialidade do profissional selecionado
            const selectedProfessional = this.professionals.find(p => String(p.id) === String(this.formData.professionalId));
            const specialty = selectedProfessional && selectedProfessional.category ? selectedProfessional.category.join(', ') : '';

            // Get the professional's colorId from meta_data
            let professionalColorId = null;
            if (selectedProfessional && selectedProfessional.meta_data && selectedProfessional.meta_data.colorId) {
                professionalColorId = selectedProfessional.meta_data.colorId;
            }

            // Encontrar o contato selecionado
            const selectedContact = this.contacts.find(c => String(c.id) === String(this.formData.contactId));
            const contactName = selectedContact ? selectedContact.name : '';

            // Preparar dados do evento para API
            const eventData = {
                title: this.formData.title.trim(),
                start: new Date(this.formData.startDateTime).toISOString(),
                end: new Date(this.formData.endDateTime).toISOString(),
                all_day: this.formData.allDay,
                description: this.formData.description || '',
                professional_id: this.formData.professionalId,
                professional_color_id: professionalColorId,
                specialty: specialty,
                contact_id: this.formData.contactId,
                client_name: contactName,
                // Converter array de attendees para string separada por vírgulas
                attendees: Array.isArray(this.formData.attendees) ? this.formData.attendees.join(', ') : this.formData.attendees
            };

            try {
                this.loading = true;
                this.loadingMessage = this.eventId ? 'Atualizando evento...' : 'Criando evento...';

                let response;

                if (this.eventId) {
                    // Atualizar evento existente
                    // console.log(`Atualizando evento ID: ${this.eventId}`, eventData);
                    response = await this.updateEvent(this.eventId, eventData);
                } else {
                    // Criar novo evento
                    // console.log('Criando novo evento:', eventData);
                    response = await this.createEvent(eventData);
                }

                // console.log('Resposta da API:', response.data);

                // Formatar dados do evento para o FullCalendar
                const formattedEvent = {
                    id: response.data.id,
                    title: response.data.title,
                    start: response.data.start,
                    end: response.data.end,
                    allDay: response.data.allDay || false,
                    extendedProps: response.data.extendedProps || {}
                };

                // Emitir evento com os dados atualizados/criados
                this.$emit('submit', formattedEvent);

                // Fechar o modal
                this.close();
            } catch (error) {
                console.error('Erro ao salvar evento:', error);
                this.handleApiError(error);
            } finally {
                this.loading = false;
            }
        },

        // Criar um novo evento
        async createEvent(eventData) {
            const headers = this.getAuthHeaders();

            return await axios.post('/api/v1/google_calendar', eventData, {
                headers,
                withCredentials: true
            });
        },

        // Atualizar um evento existente
        async updateEvent(eventId, eventData) {
            const headers = this.getAuthHeaders();

            return await axios.put(`/api/v1/google_calendar/${eventId}`, eventData, {
                headers,
                withCredentials: true
            });
        },

        // Confirmar exclusão do evento
        confirmDelete() {
            this.showDeleteConfirmation = true;
        },

        // Excluir um evento
        async deleteEvent() {
            if (!this.eventId) {
                this.error = 'ID do evento não encontrado.';
                return;
            }

            try {
                this.loading = true;
                this.loadingMessage = 'Excluindo evento...';

                const headers = this.getAuthHeaders();

                // Log ID format for debugging
                // console.log(`Excluindo evento ID: ${this.eventId} (tipo: ${typeof this.eventId})`);

                await axios.delete(`/api/v1/google_calendar/${this.eventId}`, {
                    headers,
                    withCredentials: true
                });

                // Emitir evento de exclusão
                this.$emit('delete', this.eventId);

                // Fechar o modal
                this.close();
            } catch (error) {
                console.error('Erro ao excluir evento:', error);
                this.handleApiError(error);

                // Adicionar mais informações de depuração
                // if (error.response) {
                //     console.error('Resposta de erro:', {
                //         status: error.response.status,
                //         statusText: error.response.statusText,
                //         data: error.response.data,
                //         headers: error.response.headers
                //     });
                // }
            } finally {
                this.loading = false;
                this.showDeleteConfirmation = false;
            }
        },

        // Tratamento de erros da API
        handleApiError(error) {
            if (error.response) {
                // O servidor respondeu com um status de erro
                if (error.response.data && error.response.data.error) {
                    this.error = error.response.data.error;
                } else if (error.response.status === 401) {
                    this.error = 'Sessão expirada ou usuário não autenticado. Faça login novamente.';
                } else if (error.response.status === 403) {
                    this.error = 'Sem permissão para realizar esta operação.';
                } else if (error.response.status === 404) {
                    this.error = 'Evento não encontrado. Recarregue a página e tente novamente.';
                } else {
                    this.error = `Erro ao processar a solicitação (${error.response.status}).`;
                }
            } else if (error.request) {
                // A requisição foi feita mas não houve resposta
                this.error = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
            } else {
                // Algo aconteceu ao configurar a requisição
                this.error = `Erro ao processar a solicitação: ${error.message}`;
            }
        },

        // Handle clicking outside dropdowns
        handleClickOutside(event) {
            // Check professional dropdown
            if (this.showProfessionalDropdown) {
                if (!this.$refs.professionalTrigger?.contains(event.target) && 
                    !event.target.closest('[data-dropdown="professional"]')) {
                    this.showProfessionalDropdown = false;
                    this.updateModalScrollLock();
                }
            }
            
            // Check contact dropdown
            if (this.showContactDropdown) {
                if (!this.$refs.contactTrigger?.contains(event.target) && 
                    !event.target.closest('[data-dropdown="contact"]')) {
                    this.showContactDropdown = false;
                    this.updateModalScrollLock();
                }
            }
        },

        // Update modal scroll lock based on dropdown state
        updateModalScrollLock() {
            const modalBody = document.querySelector('.modal-body');
            if (modalBody) {
                if (this.showProfessionalDropdown || this.showContactDropdown) {
                    modalBody.addEventListener('wheel', this.preventScroll, { passive: false });
                    modalBody.addEventListener('touchmove', this.preventScroll, { passive: false });
                } else {
                    modalBody.removeEventListener('wheel', this.preventScroll);
                    modalBody.removeEventListener('touchmove', this.preventScroll);
                }
            }
        },

        // Prevent scroll event
        preventScroll(e) {
            e.preventDefault();
            e.stopPropagation();
            return false;
        },

        // Fechar o modal
        close() {
            this.error = null;
            this.showDeleteConfirmation = false;
            this.showProfessionalDropdown = false;
            this.showContactDropdown = false;
            this.contactSearchQuery = '';
            this.updateModalScrollLock(); // Reset scroll state
            this.$emit('close');
        }
    },
    mounted() {
        this.initializeFormData();
        this.loadProfessionals();
        this.loadContacts();
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        // Clean up scroll event listeners
        const modalBody = document.querySelector('.modal-body');
        if (modalBody) {
            modalBody.removeEventListener('wheel', this.preventScroll);
            modalBody.removeEventListener('touchmove', this.preventScroll);
        }
    }
}
</script>

<style scoped>
/* Modal styles */
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
}

.modal-container {
    border-radius: 8px;
    max-width: 95%;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-bottom: 1px solid rgb(var(--slate-4) / 1);
}

.dark .modal-header {
    border-bottom: 1px solid rgb(var(--slate-6) / 1);
}

.modal-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
}

/* Loading overlay */
.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.8);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 10;
}

.dark .loading-overlay {
    background-color: rgba(0, 0, 0, 0.5);
}

.spinner {
    border: 3px solid rgba(0, 0, 0, 0.1);
    border-top: 3px solid #2b70e0;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}


/* Responsive layout */
@media (max-width: 768px) {
    .grid-cols-2 {
        grid-template-columns: 1fr;
    }
}
</style>
